import { apiClient } from "./api";

export interface Setting {
  id: string;
  key: string;
  value: string;
  description?: string;
  isSecret: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSettingDto {
  key: string;
  value: string;
  description?: string;
  isSecret?: boolean;
}

export interface UpdateSettingDto {
  value: string;
  description?: string;
}

export interface AzureEntraConfigField {
  value: string;
  maskedValue: string;
  isConfigured: boolean;
  isSecret: boolean;
  source: string;
}

export interface AzureEntraVaultStatus {
  isConnected: boolean;
  message: string;
}

export interface AzureSsoStatus {
  isConfigured: boolean;
  message: string;
}

export interface AppInsightsStatus {
  isConfigured: boolean;
  isConnected: boolean;
  message: string;
}

export interface AppInsightsConfig {
  instrumentationKey: AzureEntraConfigField;
  status: AppInsightsStatus;
}

export interface AzureEntraConfig {
  tenantId: AzureEntraConfigField;
  clientId: AzureEntraConfigField;
  clientSecret: AzureEntraConfigField;
  keyVaultUrl: AzureEntraConfigField;
  vaultStatus: AzureEntraVaultStatus;
  ssoStatus: AzureSsoStatus;
  appInsights: AppInsightsConfig;
}

export const settingsService = {
  /**
   * Get all settings
   */
  async getAllSettings(): Promise<Setting[]> {
    const response = await apiClient.get<{ success: boolean; data: Setting[] }>("/api/settings");
    return response.data.data;
  },

  /**
   * Get setting by ID
   */
  async getSettingById(id: string): Promise<Setting> {
    const response = await apiClient.get<{ success: boolean; data: Setting }>(
      `/api/settings/${id}`,
    );
    return response.data.data;
  },

  /**
   * Get setting by key
   */
  async getSettingByKey(key: string): Promise<Setting | null> {
    try {
      const response = await apiClient.get<{ success: boolean; data: Setting }>(
        `/api/settings/by-key/${encodeURIComponent(key)}`,
      );
      return response.data.data;
    } catch {
      return null;
    }
  },

  /**
   * Create a new setting
   */
  async createSetting(dto: CreateSettingDto): Promise<Setting> {
    const response = await apiClient.post<{ success: boolean; data: Setting }>(
      "/api/settings",
      dto,
    );
    return response.data.data;
  },

  /**
   * Update a setting
   */
  async updateSetting(id: string, dto: UpdateSettingDto): Promise<Setting> {
    const response = await apiClient.put<{ success: boolean; data: Setting }>(
      `/api/settings/${id}`,
      dto,
    );
    return response.data.data;
  },

  /**
   * Delete a setting
   */
  async deleteSetting(id: string): Promise<void> {
    await apiClient.delete(`/api/settings/${id}`);
  },

  /**
   * Get Azure Entra ID configuration
   */
  async getAzureEntraConfig(): Promise<AzureEntraConfig> {
    const response = await apiClient.get<{ success: boolean; data: AzureEntraConfig }>(
      "/api/settings/azure-entra-config",
    );
    return response.data.data;
  },
};
